import { mockGroupBuys } from '@/components/shared/marketplace/mockGroupBuyData';
import { GroupBuy } from '@/components/shared/marketplace/types';

export interface GroupBuyFilter {
  status?: 'active' | 'successful' | 'completed';
  categories?: string[];
  minParticipants?: number;
  maxDeadlineDays?: number;
  financiallyRelevant?: boolean;
}

export class GroupBuyService {
  private static instance: GroupBuyService;
  private groupBuys: GroupBuy[];
  private baseUrl: string;

  private constructor() {
    this.groupBuys = mockGroupBuys;
    this.baseUrl = import.meta.env.VITE_API_BASE_URL || '/api';
  }

  public static getInstance(): GroupBuyService {
    if (!GroupBuyService.instance) {
      GroupBuyService.instance = new GroupBuyService();
    }
    return GroupBuyService.instance;
  }

  // API Methods with fallback to mock data
  public async getAllGroupBuysAsync(): Promise<GroupBuy[]> {
    try {
      const response = await fetch(`${this.baseUrl}/group-buys`);
      if (!response.ok) {
        throw new Error(`Failed to fetch group buys: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.warn('API not available, falling back to mock data:', error);
      return this.groupBuys;
    }
  }

  public async getActiveGroupBuysAsync(): Promise<GroupBuy[]> {
    try {
      const response = await fetch(`${this.baseUrl}/group-buys/active`);
      if (!response.ok) {
        throw new Error(`Failed to fetch active group buys: ${response.statusText}`);
      }
      const allGroupBuys = await response.json();
      return allGroupBuys.filter((gb: GroupBuy) => gb.status === 'active');
    } catch (error) {
      console.warn('API not available, falling back to mock data:', error);
      return this.groupBuys.filter((gb) => gb.status === 'active');
    }
  }

  public async getGroupBuyById(id: string): Promise<GroupBuy | undefined> {
    try {
      const response = await fetch(`${this.baseUrl}/group-buys/${id}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch group buy: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.warn('API not available, falling back to mock data:', error);
      return this.groupBuys.find((gb) => gb.id === id);
    }
  }

  public async joinGroupBuy(
    groupBuyId: string,
    userId: string,
    quantity?: number,
  ): Promise<{ success: boolean; message: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/group-buys/${groupBuyId}/join`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          quantity: quantity || 1,
          timestamp: new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to join group buy: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.warn('API not available, simulating successful join:', error);
      return {
        success: true,
        message: 'Successfully joined group buy!',
      };
    }
  }

  public async createGroupBuy(groupBuyData: Partial<GroupBuy>): Promise<GroupBuy> {
    try {
      const response = await fetch(`${this.baseUrl}/group-buys`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(groupBuyData),
      });

      if (!response.ok) {
        throw new Error(`Failed to create group buy: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.warn('API not available, simulating successful creation:', error);
      const newGroupBuy: GroupBuy = {
        id: `gb-${Date.now()}`,
        title: groupBuyData.title || 'New Group Buy',
        description: groupBuyData.description || '',
        category: groupBuyData.category || 'service',
        type: groupBuyData.type || 'service',
        regularPrice: groupBuyData.regularPrice || 100,
        groupPrice: groupBuyData.groupPrice || 80,
        currency: groupBuyData.currency || 'USD',
        minParticipants: groupBuyData.minParticipants || 10,
        maxParticipants: groupBuyData.maxParticipants,
        currentParticipants: 1,
        deadline:
          groupBuyData.deadline || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        image: groupBuyData.image || '/placeholder.svg',
        icon: groupBuyData.icon || (() => null),
        organizer: groupBuyData.organizer || {
          id: 'current-user',
          name: 'Current User',
          avatar: '/placeholder.svg',
          unit: 'Unit',
          rating: 5.0,
          completedGroupBuys: 0,
        },
        vendor: groupBuyData.vendor || {
          name: 'Local Vendor',
          rating: 4.5,
          totalReviews: 100,
          isVerified: true,
          businessSince: '2020',
        },
        discountTiers: groupBuyData.discountTiers || [],
        features: groupBuyData.features || [],
        testimonials: groupBuyData.testimonials || [],
        participants: groupBuyData.participants || [],
        terms: groupBuyData.terms || [],
        nextSteps: groupBuyData.nextSteps || [],
        estimatedCompletion: groupBuyData.estimatedCompletion || '',
        status: 'active',
        tags: groupBuyData.tags || [],
      };
      return newGroupBuy;
    }
  }

  public getAllGroupBuys(): GroupBuy[] {
    return [...this.groupBuys];
  }

  public getFilteredGroupBuys(filter: GroupBuyFilter = {}): GroupBuy[] {
    let filtered = [...this.groupBuys];

    // Filter by status
    if (filter.status) {
      filtered = filtered.filter((gb) => gb.status === filter.status);
    }

    // Filter by categories
    if (filter.categories && filter.categories.length > 0) {
      filtered = filtered.filter((gb) => filter.categories!.includes(gb.category));
    }

    // Filter by minimum participants threshold
    if (filter.minParticipants !== undefined) {
      filtered = filtered.filter((gb) => gb.currentParticipants >= filter.minParticipants!);
    }

    // Filter by deadline (days from now)
    if (filter.maxDeadlineDays !== undefined) {
      const maxDate = new Date();
      maxDate.setDate(maxDate.getDate() + filter.maxDeadlineDays);

      filtered = filtered.filter((gb) => {
        const deadline = new Date(gb.deadline);
        return deadline <= maxDate;
      });
    }

    // Filter for financially relevant group buys (high value, high participation, near completion)
    if (filter.financiallyRelevant) {
      filtered = filtered.filter((gb) => {
        const savings = gb.regularPrice - gb.groupPrice;
        const progressPercent = (gb.currentParticipants / gb.minParticipants) * 100;
        const isHighValue = savings >= 50; // At least $50 savings
        const hasGoodProgress = progressPercent >= 60; // At least 60% to minimum
        const isNearDeadline = this.getDaysUntilDeadline(gb.deadline) <= 30; // Within 30 days

        return isHighValue && (hasGoodProgress || isNearDeadline);
      });
    }

    return filtered;
  }

  public getActiveGroupBuys(): GroupBuy[] {
    return this.getFilteredGroupBuys({ status: 'active' });
  }

  public getFinanciallyRelevantGroupBuys(): GroupBuy[] {
    return this.getFilteredGroupBuys({
      status: 'active',
      financiallyRelevant: true,
    });
  }

  public getGroupBuysByCategory(category: string): GroupBuy[] {
    return this.getFilteredGroupBuys({
      status: 'active',
      categories: [category],
    });
  }

  public getTotalPotentialSavings(): number {
    const activeGroupBuys = this.getActiveGroupBuys();
    return activeGroupBuys.reduce((total, gb) => {
      const savings = gb.regularPrice - gb.groupPrice;
      return total + savings * gb.currentParticipants;
    }, 0);
  }

  public getCommunityParticipationStats() {
    const activeGroupBuys = this.getActiveGroupBuys();
    const totalParticipants = activeGroupBuys.reduce((sum, gb) => sum + gb.currentParticipants, 0);
    const avgProgressPercent =
      activeGroupBuys.reduce((sum, gb) => {
        return sum + (gb.currentParticipants / gb.minParticipants) * 100;
      }, 0) / activeGroupBuys.length;

    return {
      activeGroupBuys: activeGroupBuys.length,
      totalParticipants,
      avgProgressPercent: Math.round(avgProgressPercent),
      totalPotentialSavings: this.getTotalPotentialSavings(),
    };
  }

  private getDaysUntilDeadline(deadline: string): number {
    const deadlineDate = new Date(deadline);
    const today = new Date();
    const diffTime = deadlineDate.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  public getTopGroupBuysByValue(limit: number = 5): GroupBuy[] {
    const activeGroupBuys = this.getActiveGroupBuys();
    return activeGroupBuys
      .sort((a, b) => {
        const savingsA = a.regularPrice - a.groupPrice;
        const savingsB = b.regularPrice - b.groupPrice;
        return savingsB - savingsA;
      })
      .slice(0, limit);
  }

  public getGroupBuysNearingDeadline(days: number = 7): GroupBuy[] {
    return this.getFilteredGroupBuys({
      status: 'active',
      maxDeadlineDays: days,
    });
  }

  // Format utilities for consistent display
  public formatPrice = (price: number, currency: string = 'USD'): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(price);
  };

  public formatDeadline = (deadline: string): string => {
    const date = new Date(deadline);
    const days = this.getDaysUntilDeadline(deadline);

    if (days < 0) return 'Expired';
    if (days === 0) return 'Today';
    if (days === 1) return 'Tomorrow';
    if (days <= 7) return `${days} days left`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  public calculateProgress = (current: number, target: number): number => {
    return Math.min((current / target) * 100, 100);
  };

  public calculateSavings = (regular: number, group: number, quantity: number = 1): number => {
    return (regular - group) * quantity;
  };
}

export const groupBuyService = GroupBuyService.getInstance();
